import type { HoldingChangeType } from "./types";
import { I18nUtil } from "@utils/i18n";
import { Locale } from "@utils/i18n.locale";
import Translations from "@translate/components/WhaleHoldings/utils";
import { CikChangeTypeEnum } from "@/src/features/api/memberApi";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 接口 CikChangeTypeEnum 与业务 HoldingChangeType 的映射（大佬持仓通用）
 */
const CIK_CHANGE_TYPE_TO_HOLDING: Record<CikChangeTypeEnum, HoldingChangeType> =
  {
    [CikChangeTypeEnum.INCREASED]: "increase",
    [CikChangeTypeEnum.DECREASED]: "decrease",
    [CikChangeTypeEnum.NEW_POSITION]: "new",
    [CikChangeTypeEnum.CLOSED_POSITION]: "clear",
    [CikChangeTypeEnum.UNCHANGED]: "unchanged",
  };

/**
 * 将接口 change_type 转为业务 HoldingChangeType，无值时默认 "increase"
 */
export function mapCikChangeTypeToHolding(
  changeType: CikChangeTypeEnum | undefined,
): HoldingChangeType {
  if (!changeType || !CIK_CHANGE_TYPE_TO_HOLDING[changeType]) {
    return "increase";
  }
  return CIK_CHANGE_TYPE_TO_HOLDING[changeType];
}

/**
 * 获取持仓变动类型的显示文本
 * @param changeType 变动类型
 * @returns 显示文本
 */
export const getChangeTypeText = (
  changeType: HoldingChangeType,
  locale: Locale,
): string => {
  const typeMap: Record<HoldingChangeType, string> = {
    increase: I18n.t("Added", { locale }),
    decrease: I18n.t("Trimmed", { locale }),
    new: I18n.t("Entry", { locale }),
    clear: I18n.t("Sold Out", { locale }),
    unchanged: I18n.t("Unchanged", { locale }),
  };
  return typeMap[changeType];
};

/**
 * 保留两位小数但不四舍五入，只截断
 */
const formatTruncate2Decimals = (num: number): string => {
  const factor = 100;
  const truncated = Math.trunc(num * factor) / factor;
  return truncated.toFixed(2);
};

/**
 * 数值格式化：保留 2 位小数（截断，不四舍五入），并根据大小追加 K / M / B 后缀
 * 规则：
 * - < 1万：直接显示数值，保留 2 位小数
 * - [1万, 100万)：除以 1,000 显示为 K（例如 10,000 -> 10.00K）
 * - [100万, 10亿)：除以 1,000,000 显示为 M
 * - ≥ 10亿：除以 1,000,000,000 显示为 B
 */
export function formatNumberWithSuffix2Decimals(value?: number | null): string {
  if (value == null || Number.isNaN(value)) {
    return "--";
  }
  const abs = Math.abs(value);
  const B = 1_000_000_000;
  const M = 1_000_000;
  const K_THRESHOLD = 10_000; // 1 万起始使用 K

  if (abs >= B) {
    return `${formatTruncate2Decimals(abs / B)}B`;
  }
  if (abs >= M) {
    return `${formatTruncate2Decimals(abs / M)}M`;
  }
  if (abs >= K_THRESHOLD) {
    // 例如 10,000 -> 10.00K
    return `${formatTruncate2Decimals(abs / 1_000)}K`;
  }
  return formatTruncate2Decimals(abs);
}

/**
 * 数值格式化：复用 K / M / B 逻辑，但如果小数部分全是 0，则去掉小数位
 */
export function formatNumberWithSuffixNoTrailingDecimals(
  value?: number | null,
): string {
  const formatted = formatNumberWithSuffix2Decimals(value);
  if (formatted === "--") {
    return formatted;
  }
  return formatted
    .replace(/(\.\d*?[1-9])0+([KMB]?)$/, "$1$2")
    .replace(/\.0+([KMB]?)$/, "$1");
}

/**
 * 带正负号的数值格式化：规则同 formatNumberWithSuffix2Decimals，结果前加 + / -
 * 用于持仓变动股数等需要区分增减的场景，例如 +10.00K、-5.00M、0.00
 */
export function formatNumberWithSuffix2DecimalsSigned(
  value?: number | null,
  changeType?: HoldingChangeType,
): string {
  if (value == null || Number.isNaN(value)) {
    return "--";
  }
  const abs = Math.abs(value);
  const formatted = formatNumberWithSuffix2Decimals(abs);
  // 结果为 0.00 时统一显示 "0.00"，不加正负号
  if (formatted === "0.00") {
    return "0.00";
  }

  // 根据变动类型决定符号：increase/new 前面加 +，decrease/clear 前面加 -，unchanged 不加符号
  let sign = "";
  if (changeType === "increase" || changeType === "new") {
    sign = "+";
  } else if (changeType === "decrease" || changeType === "clear") {
    sign = "-";
  } else {
    sign = "";
  }

  return `${sign}${formatted}`;
}
